import { useState, useEffect } from 'react';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { X } from 'lucide-react';

const COOKIE_KEY = 'cla_cookie_consent';

const TOGGLES = [
  { id: 'necessary', label: 'Necesarias', description: 'Esenciales para el funcionamiento del portal.', required: true },
  { id: 'analytics', label: 'Analítica', description: 'Nos ayudan a mejorar la web.' },
  { id: 'preferences', label: 'Preferencias', description: 'Recuerdan tus configuraciones.' },
  { id: 'marketing', label: 'Marketing', description: 'Para contenido personalizado.' },
];

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    preferences: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const save = (consent) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...consent, timestamp: Date.now() }));
    setVisible(false);
  };

  const acceptAll = () =>
    save({ necessary: true, analytics: true, preferences: true, marketing: true });
  const rejectAll = () =>
    save({ necessary: true, analytics: false, preferences: false, marketing: false });
  const savePrefs = () => save(prefs);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Configuración de cookies"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[60] rounded-2xl border border-[#CFE3F5] bg-white shadow-md p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <h2 className="font-display text-lg text-[#0A2540]">Cookies</h2>
        <button
          onClick={rejectAll}
          className="p-1 rounded-lg hover:bg-[#CFE3F5] transition-colors text-[#6F8AA8]"
          aria-label="Rechazar cookies no esenciales y cerrar"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      <p className="text-xs text-[#6F8AA8] leading-relaxed mb-4">
        Usamos cookies para mejorar tu experiencia. Puedes aceptar todas o gestionar tus preferencias.
        Consulta nuestra{' '}
        <button className="underline hover:text-[#1E5AA8]" onClick={() => {}}>política de cookies</button>.
      </p>

      {expanded && (
        <div className="mb-4 space-y-3">
          {TOGGLES.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <p className="text-xs font-medium text-[#0A2540]">{t.label}</p>
                <p className="text-xs text-[#6F8AA8]">{t.description}</p>
              </div>
              <Switch
                id={`cookie-${t.id}`}
                checked={prefs[t.id]}
                disabled={t.required}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, [t.id]: v }))}
                aria-label={`Cookie ${t.label}`}
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          data-testid="cookie-accept-all"
          size="sm"
          onClick={acceptAll}
          className="flex-1 rounded-full bg-[#0A2540] text-white hover:bg-[#1E5AA8] text-xs"
        >
          Aceptar todo
        </Button>
        <Button
          data-testid="cookie-reject-all"
          size="sm"
          variant="outline"
          onClick={rejectAll}
          className="flex-1 rounded-full border-[#CFE3F5] text-[#6F8AA8] hover:bg-[#CFE3F5] text-xs"
        >
          Rechazar
        </Button>
        <Button
          data-testid="cookie-manage-preferences"
          size="sm"
          variant="ghost"
          onClick={() => setExpanded(!expanded)}
          className="flex-1 rounded-full text-[#6F8AA8] hover:bg-[#CFE3F5] text-xs"
        >
          {expanded ? 'Ocultar' : 'Gestionar'}
        </Button>
      </div>

      {expanded && (
        <Button
          data-testid="cookie-save-preferences"
          size="sm"
          onClick={savePrefs}
          className="mt-2 w-full rounded-full bg-[#1E5AA8] text-white hover:bg-[#0A2540] text-xs"
        >
          Guardar preferencias
        </Button>
      )}
    </div>
  );
}
